import type { SelectWithSlim } from '../custom-select.ts';
import type { ValidationRule } from './validation-engine.js';
import type { ValidatorsResponse } from './api.ts';
import { getFormValidators } from './api.ts';

import { validationRules, getValidatorRegex, validate } from './validation-engine.js';
import { getFieldHandler } from './field-handler-factory.js';
import { executeRecaptcha, initRecaptcha } from './recaptcha.ts';

const messages = {
  pl: {
    required: 'Pole jest wymagane',
    wrongFormat: 'Niepoprawny format',
    invalidField: 'Nieprawidłowe pole',
  },
  en: {
    required: 'Mandatory field',
    wrongFormat: 'Wrong format',
    invalidField: 'Invalid field',
  },
} as const;
const language = (document.documentElement.lang || 'pl') as keyof typeof messages;
const i18n = messages[language];

export class FormGenerator {
  private section: Element;
  private form: HTMLFormElement;
  private readonly successComponent: Element | null;
  private readonly recaptchaInput: Element | null;
  private readonly sessionExpired: Element | null;
  private readonly failure: Element | null;
  private fields: NodeListOf<Element>;
  private fieldRules: Map<Element, ValidationRule[]>;
  private globalValidators: ValidatorsResponse | undefined;

  constructor(section: Element) {
    this.section = section;
    this.form = this.section.querySelector('form') as HTMLFormElement;
    this.recaptchaInput = this.section.querySelector('.recaptcha-input');

    this.successComponent = this.section.querySelector('.success');
    this.sessionExpired = this.section.querySelector('.session-expired');
    this.failure = this.section.querySelector('.failure');
    this.fields = this.section.querySelectorAll('[required], [data-validation]');
    this.fieldRules = new Map();
    this.globalValidators = undefined;
  }

  async init(): Promise<void> {
    const visibleFormState = this.scrollToProcessedForm();
    if (this.recaptchaInput) {
      await initRecaptcha();
    }

    if (!visibleFormState) {
      await this.parseAllRules();
      this.attachListeners();
    }
  }

  loading(isLoading = true) {
    this.section.querySelector('.spinner-wrapper')?.toggleAttribute('hidden', !isLoading);
  }

  scrollToProcessedForm() {
    const visible = [this.successComponent, this.sessionExpired, this.failure].find(Boolean);
    visible?.scrollIntoView();

    return visible;
  }

  async parseAllRules(): Promise<void> {
    this.globalValidators = await getFormValidators();

    this.fields.forEach((field) => {
      this.fieldRules.set(field, this.getFieldRules(field as HTMLElement));
    });
  }

  attachListeners() {
    this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
    this.form.addEventListener('reset', () => this.handleFormReset());

    this.section.addEventListener(
      'blur',
      (e) => {
        const target = e.target as HTMLElement;

        if (this.isValidatable(target)) {
          this.validateField(target);
        }
      },
      true,
    );

    this.section.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;

      if (this.isValidatable(target) && /^(checkbox|radio)$/.test(target.type)) {
        this.validateField(target);
      }
    });

    this.section.addEventListener('slim:change', (e) => {
      const target = e.target as HTMLElement;

      this.validateField(target);
    });
  }

  isValidatable(el: HTMLElement) {
    return el.hasAttribute('required') || el.hasAttribute('data-validation');
  }

  async handleFormSubmit(e: Event): Promise<false | undefined> {
    e.preventDefault();
    this.loading(true);

    const errorsCount = this.validateForm();
    if (errorsCount > 0) {
      this.loading(false);
      this.handleGlobalError();
      return false;
    }

    if (this.recaptchaInput) {
      await executeRecaptcha(this.recaptchaInput as HTMLInputElement);
    }

    this.form.submit();
    this.loading(false);
  }

  handleFormReset() {
    const fields = this.section.querySelectorAll('[required], [data-validation]');
    const selects = this.section.querySelectorAll('select');
    fields.forEach((field) => {
      const handler = getFieldHandler(field as HTMLElement);

      handler.removeError();
    });

    selects.forEach((select) => {
      (select as SelectWithSlim)?.slim?.setSelected('', false);
    });

    this.handleGlobalError(true);
  }

  validateForm() {
    return Array.from(this.fields).filter((f) => !this.validateField(f as HTMLElement)).length;
  }

  validateField(el: HTMLElement) {
    const handler = getFieldHandler(el);
    const rules = this.fieldRules.get(el) || this.getFieldRules(el);
    const result = validate(el as HTMLInputElement, rules);

    if (result.isValid) {
      handler.showSuccess();
    } else {
      handler.showError(result.message ?? '');
    }

    return result.isValid;
  }

  getFieldRules(el: HTMLElement): ValidationRule[] {
    const rules: ValidationRule[] = [];

    if (el.hasAttribute('required')) {
      rules.push({
        name: 'required',
        message: el.dataset.errorMsg || i18n.required,
      });
    }

    const validationData = el.dataset.validation;
    if (validationData) {
      const requestedRules = validationData.split(',').map((s) => s.trim());
      requestedRules.forEach((ruleName) => {
        const ruleConfig = this.globalValidators?.nodes?.find((v) => v.name === ruleName);
        const expression = getValidatorRegex(ruleName, this.globalValidators);

        if (ruleConfig) {
          rules.push({
            name: ruleConfig.name,
            type: 'RegexValidator',
            expression,
            message: i18n.wrongFormat,
          });
        } else if (validationRules[ruleName]) {
          rules.push({ name: ruleName, message: i18n.invalidField });
        }
      });
    }

    return rules;
  }

  handleGlobalError(isValid = false): void {
    const errorBox = this.section?.querySelector('.error-box');

    if (!errorBox) {
      return;
    }

    if (isValid) {
      errorBox.classList.add('hide');
      this.section.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    errorBox.classList.add('hide');
    setTimeout(() => {
      errorBox.classList.remove('hide');
      this.section.scrollIntoView({ behavior: 'smooth' });
    }, 10);
  }
}

export function initFormGenerators(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('.fc-form-section').forEach((container) => {
    const formGenerator = new FormGenerator(container);
    void formGenerator.init();
  });
}
